import type { ChatImage } from '@/components/layout/chat-pane.types'

/** Match the server vision cap. Pixels above this are discarded by the model. */
export const CHAT_VISION_MAX_EDGE = 1568

export function visionPixelSize(
  width: number,
  height: number,
  maxEdge = CHAT_VISION_MAX_EDGE,
): { width: number; height: number } {
  const longEdge = Math.max(width, height)
  if (!longEdge || longEdge <= maxEdge) {
    return { width: Math.max(1, width), height: Math.max(1, height) }
  }
  const scale = maxEdge / longEdge
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

/**
 * Shrink a pasted screenshot before it is staged or sent as base64.
 * GIF stays as-is. If canvas is unavailable, the original bytes are kept
 * and the API still resizes before the model call.
 */
export function resizeChatImageFile(file: File): Promise<ChatImage> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error || new Error('Could not read image'))
    reader.onload = () => {
      const originalUrl = String(reader.result || '')
      const mediaType = (file.type || 'image/png') as ChatImage['mediaType']
      const id = `img-${Date.now()}-${Math.random().toString(36).slice(2)}`
      const keepOriginal = () => {
        const base64 = originalUrl.split(',')[1] || ''
        resolve({ id, dataUrl: originalUrl, base64, mediaType })
      }
      if (mediaType === 'image/gif' || typeof document === 'undefined') {
        keepOriginal()
        return
      }
      const img = new Image()
      img.onload = () => {
        const size = visionPixelSize(img.naturalWidth, img.naturalHeight)
        const canvas = document.createElement('canvas')
        canvas.width = size.width
        canvas.height = size.height
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          keepOriginal()
          return
        }
        ctx.drawImage(img, 0, 0, size.width, size.height)
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8)
        const base64 = dataUrl.split(',')[1] || ''
        resolve({ id, dataUrl, base64, mediaType: 'image/jpeg' })
      }
      img.onerror = () => reject(new Error('Could not read image'))
      img.src = originalUrl
    }
    reader.readAsDataURL(file)
  })
}
